import { en } from "./locales/en";
import { id } from "./locales/id";
import type { Locale, TranslationKey, TranslationParams } from "./types";

/**
 * Pure translation-resolution logic, kept separate from the React
 * context so it can be unit-tested without rendering. The context
 * (``context.tsx``) is a thin stateful wrapper over these.
 */

/**
 * localStorage key for the persisted locale preference. The
 * dashboard does not auto-detect the browser language —
 * Indonesian is the deliberate default for the domestic market.
 */
export const STORAGE_KEY = "monago.locale";

export const TRANSLATIONS: Record<Locale, unknown> = { id, en };

/**
 * Walk a dot-notation key into the translation tree. Returns the
 * key path itself when the path does not resolve to a string, so
 * a missing translation is visible rather than blank.
 */
export function resolveKey(tree: unknown, key: string): string {
  const value = key.split(".").reduce<unknown>((acc, segment) => {
    if (acc && typeof acc === "object" && segment in acc) {
      return (acc as Record<string, unknown>)[segment];
    }
    return undefined;
  }, tree);
  return typeof value === "string" ? value : key;
}

/**
 * Replace ``{name}`` placeholders in a template with the matching
 * param values. A template with no placeholders is returned
 * unchanged.
 */
export function interpolate(
  template: string,
  params?: TranslationParams
): string {
  if (!params) return template;
  return Object.entries(params).reduce(
    (acc, [name, value]) => acc.split(`{${name}}`).join(String(value)),
    template
  );
}

/**
 * Resolve a translation key for a given locale, applying
 * interpolation. The single entry point used by the context's
 * ``t`` function.
 */
export function translate(
  locale: Locale,
  key: TranslationKey,
  params?: TranslationParams
): string {
  const template = resolveKey(TRANSLATIONS[locale], key);
  return interpolate(template, params);
}

/**
 * Read the persisted locale preference. Falls back to Indonesian
 * for an unset, invalid, or unavailable store.
 */
export function readStoredLocale(): Locale {
  if (typeof window === "undefined") return "id";
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "en" ? "en" : "id";
  } catch {
    // localStorage may be unavailable (Safari private mode, tests).
    return "id";
  }
}

/**
 * Persist the locale preference. Best-effort — a quota error or
 * private-mode restriction is swallowed so in-memory state still
 * updates.
 */
export function persistLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Non-fatal.
  }
}
